import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect } from "react";

import { useAuth } from "@/context/auth-context";
import useLocalStorageState, { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";
import type { OnboardingPath } from "@/routes/onboarding/constants/path";
import type { FormFields } from "@/routes/onboarding/types";

export interface OnboardingDestination {
  userId: string;
  workspaceId: number;
  promptTemplateId: number | null;
  formValues?: FormFields;
}

interface OnboardingFlowContextValue {
  onboardingPath: OnboardingPath | null;
  setOnboardingPath: Dispatch<SetStateAction<OnboardingPath | null>>;
  onboardingDestination: OnboardingDestination | null;
  setOnboardingDestination: Dispatch<SetStateAction<OnboardingDestination | null>>;
  clearOnboardingFlow: () => void;
}

const OnboardingFlowContext = createContext<OnboardingFlowContextValue | null>(null);

export const OnboardingFlowProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();
  const currentUserId = auth?.user?.id ? String(auth.user.id) : null;
  const [storedOnboardingPath, setOnboardingPath] = useLocalStorageState<OnboardingPath | null>(
    LOCAL_STORAGE_KEYS.ONBOARDING_PATH,
    null,
  );
  const [storedOnboardingDestination, setOnboardingDestination] =
    useLocalStorageState<OnboardingDestination | null>(
      LOCAL_STORAGE_KEYS.ONBOARDING_DESTINATION,
      null,
    );
  const ownsStoredFlow = storedOnboardingDestination?.userId === currentUserId;
  const onboardingPath = ownsStoredFlow ? storedOnboardingPath : null;
  const onboardingDestination = ownsStoredFlow ? storedOnboardingDestination : null;

  const clearOnboardingFlow = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ONBOARDING_PATH);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ONBOARDING_DESTINATION);
    setOnboardingPath(null);
    setOnboardingDestination(null);
  }, [setOnboardingDestination, setOnboardingPath]);

  useEffect(() => {
    if (storedOnboardingDestination && !ownsStoredFlow) {
      clearOnboardingFlow();
    }
  }, [clearOnboardingFlow, ownsStoredFlow, storedOnboardingDestination]);

  return (
    <OnboardingFlowContext.Provider
      value={{
        onboardingPath,
        setOnboardingPath,
        onboardingDestination,
        setOnboardingDestination,
        clearOnboardingFlow,
      }}
    >
      {children}
    </OnboardingFlowContext.Provider>
  );
};

export const useOnboardingFlow = () => {
  const context = useContext(OnboardingFlowContext);

  if (!context) {
    throw new Error("useOnboardingFlow must be used within OnboardingFlowProvider");
  }

  return context;
};
